/**
 * Approval Actions Hook
 * Handles approve/reject/reopen logic for proposals
 */

import { useState, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';

export const useApprovalActions = (onSuccess) => {
  const [processing, setProcessing] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [approvalAction, setApprovalAction] = useState('approved');
  const [comments, setComments] = useState('');
  const [showReopenConfirm, setShowReopenConfirm] = useState(null);

  /**
   * Initiate approve action
   */
  const handleApprove = useCallback((proposal) => {
    setSelectedProposal(proposal);
    setApprovalAction('approved');
    setComments('');
    setShowApprovalModal(true);
  }, []);

  /**
   * Initiate reject action
   */
  const handleReject = useCallback((proposal) => {
    setSelectedProposal(proposal);
    setApprovalAction('rejected');
    setComments('');
    setShowApprovalModal(true);
  }, []);

  /**
   * Confirm and submit approval/rejection
   */
  const confirmApproval = useCallback(async () => {
    if (!selectedProposal) return;

    try {
      setProcessing(true);
      
      // Process each service in the proposal
      const approvalPromises = selectedProposal.services.map(service =>
        api.post(`/scenarios/${service.scenario_id}/approve`, {
          action: approvalAction,
          comments: comments.trim()
        })
      );

      await Promise.all(approvalPromises);

      const actionText = approvalAction === 'approved' ? 'approved' : 'rejected';
      toast.success(`✅ Proposal ${actionText} successfully!`);
      
      setShowApprovalModal(false);
      setSelectedProposal(null);
      setComments('');
      
      // Trigger refresh
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('[ApprovalActions] Error processing approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setProcessing(false);
    }
  }, [selectedProposal, approvalAction, comments, onSuccess]);

  /**
   * Initiate reopen confirmation
   */
  const handleReopen = useCallback((proposal) => {
    setShowReopenConfirm(proposal.collection_id);
  }, []);

  /**
   * Confirm and execute reopen
   */
  const confirmReopen = useCallback(async (proposal) => {
    try {
      setProcessing(true);
      
      // Reopen each service in the proposal
      const reopenPromises = proposal.services.map(service =>
        api.post(`/scenarios/${service.scenario_id}/reopen`, {
          comments: 'Reopened by approver'
        })
      );

      await Promise.all(reopenPromises);

      toast.success('📂 Proposal reopened successfully!');
      setShowReopenConfirm(null);
      
      // Trigger refresh
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('[ApprovalActions] Error reopening proposal:', error);
      toast.error(error.response?.data?.detail || 'Failed to reopen proposal');
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  /**
   * Cancel reopen confirmation
   */
  const cancelReopen = useCallback(() => {
    setShowReopenConfirm(null);
  }, []);

  /**
   * Close approval modal
   */
  const closeApprovalModal = useCallback(() => {
    setShowApprovalModal(false);
    setSelectedProposal(null);
    setComments('');
  }, []);

  return {
    // State
    processing,
    showApprovalModal,
    selectedProposal,
    approvalAction,
    comments,
    showReopenConfirm,
    
    // Actions
    handleApprove,
    handleReject,
    confirmApproval,
    handleReopen,
    confirmReopen,
    cancelReopen,
    closeApprovalModal,
    
    // Setters (for advanced usage like bulk actions)
    setShowApprovalModal,
    setSelectedProposal,
    setApprovalAction,
    setComments,
    setProcessing
  };
};
